import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import {
  type CartProductDetails,
  type TablescapeState,
} from "@tablescape/state";
import { ObjectKeys } from "@tablescape/utilities";

type CartActiveProductDetails = CartProductDetails & {
  clearProductDetails: () => void;
  setProductDetails: Dispatch<SetStateAction<CartProductDetails>>;
  toggleCartDrawer: (
    ui: Partial<TablescapeState["ui"]>,
    productDetails?: CartProductDetails
  ) => void;
};

const initialProductDetails = {
  activeOptions: undefined,
  activeProduct: undefined,
  activeUpsell: undefined,
  isGiftWithPurchase: false,
  isProductUpdate: false,
  selectedOptions: undefined,
};

const CartActiveProductContext = createContext<
  CartActiveProductDetails | undefined
>(undefined);

type Props = {
  value?: CartProductDetails;
};

function CartActiveProductProvider({
  children,
  value: providedValue,
}: PropsWithChildren<Props>) {
  const [productDetails, setProductDetails] = useState<CartProductDetails>(
    providedValue ?? initialProductDetails
  );
  const clearProductDetails = useCallback(() => {
    setProductDetails(initialProductDetails);
  }, []);

  const toggleCartDrawer = useCallback(
    (
      ui: Partial<TablescapeState["ui"]>,
      productDetails?: CartProductDetails
    ) => {
      requestAnimationFrame(() => {
        const currentState = TablescapeContext.getSnapshot();

        const hasStateChanges = ObjectKeys(ui).some((key) => {
          return ui[key] !== currentState.ui[key];
        });

        if (hasStateChanges) {
          TablescapeContext.emit(TablescapeEvent.TOGGLE_CART, {
            ui: {
              ...currentState.ui,
              ...ui,
            },
          });
        }

        if (productDetails) {
          setProductDetails(productDetails);
        }
      });
    },
    []
  );

  const value = useMemo<CartActiveProductDetails>(
    () => ({
      ...productDetails,
      clearProductDetails,
      setProductDetails,
      toggleCartDrawer,
    }),
    [clearProductDetails, productDetails, toggleCartDrawer]
  );

  return (
    <CartActiveProductContext.Provider value={value}>
      {children}
    </CartActiveProductContext.Provider>
  );
}

function useCartActiveProduct() {
  const context = useContext<CartActiveProductDetails | undefined>(
    CartActiveProductContext
  );

  if (context === undefined) {
    throw new Error(
      "useCartActiveProduct must be used within a CartActiveProductProvider"
    );
  }

  return context;
}

export { CartActiveProductProvider, useCartActiveProduct };
